import type { FieldSpecOf } from 'v5/core/entity-schema/entitySchema.types';
import type {
  CoreColumnChange,
  FieldCodec,
  NameValue
} from 'v5/core/entity-values/values.types';
import { validation } from './validation';

/** Whitespace-only is empty: legacy `SHORT_TEXT.jsx` trims what is typed. */
function isBlank(text: string): boolean {
  return text.trim() === '';
}

function isEmpty(value: NameValue, spec: FieldSpecOf<'name'>): boolean {
  return spec.layout === 'single'
    ? isBlank(value.first_name)
    : isBlank(value.first_name) && isBlank(value.last_name);
}

/** `first_name` (+ `last_name` for the two-field layout), trimmed on save. */
export const nameCodec: FieldCodec<'name'> = {
  decode(entity) {
    return {
      first_name: entity.first_name ?? '',
      last_name: entity.last_name ?? ''
    };
  },
  encode(value, spec) {
    const changes: CoreColumnChange[] = [
      { column: 'first_name', value: value.first_name.trim() }
    ];
    if (spec.layout === 'first-last') {
      changes.push({ column: 'last_name', value: value.last_name.trim() });
    }
    return { type: 'core-changes', changes };
  },
  empty() {
    return { first_name: '', last_name: '' };
  },
  isEmpty,
  validate(value, spec) {
    if (!spec.required) return validation.valid();
    if (isEmpty(value, spec)) return validation.required();
    if (spec.layout === 'first-last') {
      if (isBlank(value.first_name)) {
        return validation.invalid({
          code: 'part-required',
          part: 'first-name',
          at: 'first_name'
        });
      }
      if (isBlank(value.last_name)) {
        return validation.invalid({
          code: 'part-required',
          part: 'last-name',
          at: 'last_name'
        });
      }
    }
    return validation.valid();
  }
};
